/**
 * `/auth/handoff`: one-tap sign-in from runebound.gg and the app (owner decision Q11). Exchanges the
 * fragment's single-use token hash for a session (`verifyOtp({ token_hash, type: 'magiclink' })`), drops
 * the fragment from the address bar, and continues to the target (`/play?deck=…`).
 */
import { LOOK, Notice } from '@play/ui';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { parseHandoff } from '../auth/handoff';
import { SignInPanel } from '../auth/SignInPanel';
import { getSupabase } from '../lib/supabase';

/** The hand-off landing page. */
export function HandoffPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  const [next, setNext] = useState('/play');

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const handoff = parseHandoff(window.location.hash, window.location.search, window.location.pathname);
    // Never leave the token in the address bar or history.
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (!handoff) {
      setError('This sign-in link is incomplete.');
      return;
    }
    setNext(handoff.next);
    const sb = getSupabase();
    if (!sb) {
      setError('This build is not connected to Runebound.');
      return;
    }
    void sb.auth.verifyOtp({ token_hash: handoff.tokenHash, type: 'magiclink' }).then(({ error: e }) => {
      if (e) {
        setError(
          e.message.includes('expired') || e.message.includes('invalid')
            ? 'This sign-in link has expired or was already used.'
            : e.message,
        );
        return;
      }
      void navigate({ href: handoff.next, replace: true });
    });
  }, [navigate]);

  if (error) {
    return (
      <div className="mx-auto max-w-[420px] pt-14 flex flex-col gap-4">
        <Notice tone="bad" icon={<AlertCircle size={15} />}>
          <p className="font-semibold">Couldn’t sign you in</p>
          <p style={{ color: LOOK.sub }}>{error} Sign in below to continue.</p>
        </Notice>
        <SignInPanel next={next} />
      </div>
    );
  }
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 py-28 text-[13px]"
      style={{ color: LOOK.sub }}
      role="status"
    >
      <span
        className="w-11 h-11 rounded-[11px] inline-flex items-center justify-center"
        style={{ background: 'rgba(124,92,255,0.14)', color: '#b9adff' }}
      >
        <Loader2 size={20} className="animate-spin motion-reduce:animate-none" />
      </span>
      Signing you in with Runebound…
    </div>
  );
}
